import React, { useState, useCallback, useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Button as BtnPrimary } from "@talentbait/shared-components";
import TextareaAutosize from "react-textarea-autosize";
import _ from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";

import UploadPhotoBtn from "../../../Components/UploadPhotoBtn";
import { AttachIcon } from "./Svg";
import Actions from "../../../Actions";
import logger from "../../../utils/LoggerFrontend";
import {
  isCampaignManagerAvailable,
  getNextAvailableCampaignManagers
} from "../../../utils/Helpers";
import { getCompanyImageFolderName } from "../../../utils/GetCompanyFolderName";
import { withRefluxStore, StatusStore, PreviewStore } from "../../../Stores";
import { renderLineBreaks } from "../../../utils/Helpers__Frontend";
import { Tooltip } from "../../../Components/Overlays";

const SURVEY_ID = 34664368;

const newMessage = keyframes`
  0% {
    max-height: 0;
    padding: 0 16px;
  }
  25% {
    background-color: #f2f2f2;
    transform: scale(1.05);
  }
`;
const SupportPanelWrapper = styled.div`
  width: 100%;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #e8e8eb;
  font-family: Rubik;
  text-align: left;
  margin-top: 16px;
`;
const SupportHeader = styled.div`
  padding: 16px;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #e8e8eb;
  box-shadow: 0px 0px 15px 0px #b7b7b7;
`;
const SupportTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.04em;
  color: #536586;
  margin-bottom: 12px;
  text-transform: uppercase;
`;
const SupportPerson = styled.div`
  display: flex;
  align-items: center;
`;
const SupportImage = styled.img`
  width: 40px;
  height: 100%;
  margin-right: 16px;
  border-radius: 100%;
`;
const SupportNameWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const SupportName = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #000000;
`;
const SupportStatus = styled.div`
  margin-left: 4px;
  background-color: ${(props) => (props.available ? "#27dd8b" : "#ffb40b")};
  width: 8px;
  height: 8px;
  border-radius: 100%;
`;
const SupportPosition = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  color: #1e1f46;
`;
const User = styled.div`
  display: flex;
  align-items: center;
`;
const UserImage = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 8px;
  border-radius: 100%;
  object-fit: cover;
`;
const UserName = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 28px;
  color: #333333;
  margin-right: 4px;
`;

const MessageTime = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 28px;
  color: #75849e;
`;
const Messages = styled.div`
  max-height: 250px;
  overflow-y: scroll;
`;
const MessageWindow = styled.div`
  animation: ${newMessage} 0.3s ease-in-out;
  overflow: scroll;
  padding: 12px 16px;
  &:not(:first-child) {
    border-top: 1px solid #e8e8eb;
  }
`;
const Message = styled.div`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: #1e1f46;
`;
const MessageAttachment = styled.div`
  max-width: 40px;
  max-height: 30px;
`;
const InputForm = styled.form`
  border-bottom: 1px solid #e8e8eb;
`;
const ChatInput = styled(TextareaAutosize)`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  color: #1e1f4680;
  padding: 12px 16px 0;
  width: 100%;
  border: none;
  resize: none;
`;

const ChatActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

const ChatSurvey = styled.a`
  color: #1e22ff;
  display: block;
  font-size: 13px;
  letter-spacing: 0px;
  line-height: 1.2;
  padding: 16px;
  text-align: center;
`;

const UploadBoxLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px dashed #e8e8eb;
  background: white;
  width: 100%;
  border-radius: 8px;
  transition: all 0.2s;
  margin: 0;
  padding: 4px 10px;
  cursor: pointer;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: center;
  color: #000000;
`;
const UploadPhotoButtonWrapper = styled.div`
  .upload-btn-wrap {
    background: transparent !important;
    margin: 0;
    padding: 0;
    width: 100%;
    &:hover {
      ${UploadBoxLabel} {
        background: light-grey;
      }
    }
  }
`;

const CampaignCreateSupportPanel = ({ campaignManagers, campaignData = {}, userProfile }) => {
  // Defines use transaltion object
  const { t } = useTranslation();

  const {
    id: campaignId = "",
    campaignManager: campaignFrontUserId,
    organizationId: orgaId,
    companyId,
    orgaName,
    companyName
  } = campaignData || {};

  const [isLoadingComments, setIsLoadingComments] = useState(true);
  const [comments, setComments] = useState([]);
  const [attachment, setAttachment] = useState();
  const [message, setMessage] = useState("");

  const campaignManager = useMemo(() => {
    if (campaignFrontUserId && campaignManagers.length) {
      const availableCMs = getNextAvailableCampaignManagers(campaignManagers);
      const campaignManager = campaignManagers.find(
        (manager) => manager.frontUserId === campaignFrontUserId
      );
      if (campaignManager) {
        campaignManager.available = isCampaignManagerAvailable(campaignManager);
        return campaignManager;
      } else if (availableCMs?.length) {
        availableCMs[0].available = true;
        return availableCMs[0];
      } else {
        const campaignManager = campaignManagers.find((manager) => manager.frontUserId === "diana");
        campaignManager.available = isCampaignManagerAvailable(campaignManager);
        return campaignManager;
      }
    }
  }, [campaignFrontUserId, campaignManagers]);

  useEffect(() => {
    // get campaign managers
    Actions.getAllCampaignManagers();
  }, []);

  // Get comments
  useEffect(() => {
    if (orgaId && campaignId) {
      Actions.fetchAllComments(orgaId, campaignId);
    }

    const loadHandler = Actions.fetchAllComments.completed.listen((data) => {
      logger.debug("AdminCampaignSidebar:fetchAllComments:completed", "called...", {
        data
      });
      let fetchedComments = _.map(
        data || [],
        ({
          userrole = "enduser",
          userName,
          comment = "",
          createDate,
          avatarUrl,
          meta: { url = "", eventName, eventStatus } = {}
        }) => {
          let message = comment;
          if (eventName == "preview" && eventStatus == "approved") {
            message = `✅ ${t("The ad preview has been released")}`;
          }
          return {
            from: userName,
            message,
            userrole,
            createdDate: moment(createDate).format(),
            avatarUrl,
            attachmentUrl: url
          };
        }
      );
      fetchedComments = _.orderBy(fetchedComments, ["createdDate"], ["desc"]);
      setIsLoadingComments(false);
      setComments(fetchedComments);
    });

    const loadHandlerFailed = Actions.fetchAllComments.failed.listen((error) => {
      logger.log("error:SupportPannel:fetchAllComments:failed", "called...", { error });
      setIsLoadingComments(false);
    });

    return () => {
      loadHandler();
      loadHandlerFailed();
    };
  }, [orgaId, campaignId]);

  const sendMessage = useCallback(
    (event) => {
      event.preventDefault();
      const userrole = userProfile.superRank ? "admin" : "enduser";
      const url = (attachment && attachment.url) || "";
      logger.log("SupportPannel:sendMessage", "called", {
        orgaId,
        campaignId,
        message,
        userName: userProfile.userName,
        userrole,
        userId: userProfile.userId,
        attachment
      });

      if (window.analytics) {
        window.analytics.track("Message Sent", {
          source: "Support panel",
          campaignId: campaignId,
          userId: userProfile.userId,
          organizationId: orgaId,
          message: message
        });
      }

      Actions.storeComment(
        orgaId,
        campaignId,
        message,
        userProfile.userName,
        userrole,
        userProfile.userId,
        {
          url,
          initiator: "user",
          type: "comment",
          eventName: "comment",
          eventStatus: "posted"
        }
      );
      const newCommentData = {
        from: userProfile.userName,
        message,
        userrole,
        createdDate: moment(),
        attachmentUrl: url
      };
      setComments([newCommentData].concat(comments));
      setAttachment(undefined);
      if (userProfile.superRank !== "admin") {
        Actions.changeCampaignData(orgaId, campaignId, "requiresAttention", true);
      }
      setMessage("");
    },
    [orgaId, campaignId, comments, setComments, attachment, userProfile, message]
  );

  const { folderName } = getCompanyImageFolderName({
    orgaId,
    companyId,
    orgaName,
    companyName
  });

  if (!campaignManager) {
    return "";
  }

  function handleSurveyClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (window.Intercom) {
      window.Intercom("startSurvey", SURVEY_ID);
    }
  }

  return (
    <SupportPanelWrapper>
      <SupportHeader>
        <SupportTitle>{t("Your personal support")}</SupportTitle>

        <SupportPerson>
          <SupportImage src={campaignManager.photo} alt="support person" />
          <div>
            <Tooltip label={t("Available soon")} enabled={!campaignManager.available}>
              <SupportNameWrapper>
                <SupportName>{campaignManager.userName}</SupportName>
                <SupportStatus available={campaignManager.available + ""}></SupportStatus>
              </SupportNameWrapper>
            </Tooltip>
            <SupportPosition>{t("Campaign Manager")}</SupportPosition>
          </div>
        </SupportPerson>
      </SupportHeader>

      <InputForm onSubmit={sendMessage}>
        <ChatInput
          name="chatInput"
          minRows={3}
          placeholder={t("Feel free to ask us your question or share your wishes with us...")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <ChatActions>
          <UploadPhotoButtonWrapper type="button" borderRadius="100%">
            <UploadPhotoBtn
              folderName={folderName}
              context={`orgaId=${orgaId}|campaignId=${campaignId}|source=comments`}
              showPreview={attachment}
              label="+ Datei hochladen"
              showLoading={true}
              handlePictureChanged={setAttachment}
              allowImages={true}
              allowVideos={true}
              buttonText={
                <UploadBoxLabel>
                  <AttachIcon />
                </UploadBoxLabel>
              }
            />
          </UploadPhotoButtonWrapper>

          <BtnPrimary
            px={"10px"}
            disabled={!message}
            size="medium"
            variant="outline"
            type="tertiary"
            mr={"5px"}
          >
            {t("Submit")}
          </BtnPrimary>
        </ChatActions>

        <ChatSurvey href={`?intercom_survey_id=${SURVEY_ID}`} onClick={handleSurveyClick}>
          {t("Help us to improve TalentBait")}
        </ChatSurvey>
      </InputForm>

      <Messages>
        {comments.map(({ from, createdDate, message, avatarUrl, attachmentUrl }) => (
          <MessageWindow key={createdDate}>
            <User>
              {avatarUrl && <UserImage src={avatarUrl} />}
              <UserName>{from}</UserName>
              <MessageTime>{moment(createdDate).fromNow()}</MessageTime>
            </User>
            <Message> {renderLineBreaks(message, createdDate)} </Message>
            {attachmentUrl && (
              <MessageAttachment>
                {attachmentUrl.includes(".mp4") ? (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video style={{ maxWidth: "40px", maxHeight: "30px" }}>
                    <source src={attachmentUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img src={attachmentUrl} />
                )}
              </MessageAttachment>
            )}
          </MessageWindow>
        ))}
      </Messages>
    </SupportPanelWrapper>
  );
};

export default withRefluxStore(
  CampaignCreateSupportPanel,
  [StatusStore, PreviewStore],
  ["campaignData", "campaignManagers"]
);
